import { api } from "@stoneflow/backend/convex/_generated/api"
import { createFileRoute, redirect } from "@tanstack/react-router"
import { useMutation, useQuery } from "convex/react"
import { Trash2 } from "lucide-react"
import { RouteError } from "#/components/route-error"
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "#/components/ui/alert-dialog"
import { Badge } from "#/components/ui/badge"
import { Button } from "#/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "#/components/ui/card"
import { Skeleton } from "#/components/ui/skeleton"
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "#/components/ui/table"

function formatExpiry(ts: number): string {
	const diff = ts - Date.now()
	if (diff <= 0) return "expired"
	const hours = Math.floor(diff / 3_600_000)
	const mins = Math.floor((diff % 3_600_000) / 60_000)
	if (hours > 0) return `in ${hours}h ${mins}m`
	return `in ${mins}m`
}

export const Route = createFileRoute("/_authenticated/admin/demo-sessions")({
	beforeLoad: ({ context }) => {
		const roles = (context as { roles?: string[] }).roles ?? []
		// Super admin only.
		if (!roles.includes("platform_admin")) {
			throw redirect({ to: "/admin" })
		}
	},
	errorComponent: RouteError,
	component: DemoSessionsPage,
})

const statusVariant: Record<string, "default" | "secondary" | "destructive"> = {
	active: "default",
	expired: "destructive",
	cleaning: "secondary",
	cleaned: "secondary",
}

function DemoSessionsPage() {
	const sessions = useQuery(api.demo.session.listAll)
	const forceCleanup = useMutation(api.demo.session.forceCleanup)
	const purgeCleaned = useMutation(api.demo.session.purgeCleaned)

	if (sessions === undefined) {
		return <DemoSessionsSkeleton />
	}

	const activeCount = sessions.filter((s) => s.status === "active").length
	const pendingCount = sessions.filter(
		(s) => s.status === "expired" || s.status === "cleaning",
	).length
	const cleanedCount = sessions.filter((s) => s.status === "cleaned").length

	return (
		<div className="space-y-6">
			<div className="grid gap-4 sm:grid-cols-3">
				<Card size="sm">
					<CardContent className="flex items-center gap-3 pt-4">
						<div>
							<p className="text-2xl font-medium">{activeCount}</p>
							<p className="text-technical">Active</p>
						</div>
					</CardContent>
				</Card>
				<Card size="sm">
					<CardContent className="flex items-center gap-3 pt-4">
						<div>
							<p className="text-2xl font-medium">{pendingCount}</p>
							<p className="text-technical">Pending Cleanup</p>
						</div>
					</CardContent>
				</Card>
				<Card size="sm">
					<CardContent className="flex items-center gap-3 pt-4">
						<div>
							<p className="text-2xl font-medium">{sessions.length}</p>
							<p className="text-technical">Total</p>
						</div>
					</CardContent>
				</Card>
			</div>

			<Card>
				<CardHeader className="flex flex-row items-center justify-between">
					<CardTitle>Demo Sessions</CardTitle>
					{cleanedCount > 0 && (
						<AlertDialog>
							<AlertDialogTrigger render={<Button variant="outline" size="sm" />}>
								Purge {cleanedCount} cleaned
							</AlertDialogTrigger>
							<AlertDialogContent>
								<AlertDialogHeader>
									<AlertDialogTitle>Purge cleaned sessions?</AlertDialogTitle>
									<AlertDialogDescription>
										This will permanently remove {cleanedCount} cleaned demo session records. Their
										Clerk orgs and data have already been deleted.
									</AlertDialogDescription>
								</AlertDialogHeader>
								<AlertDialogFooter>
									<AlertDialogCancel>Cancel</AlertDialogCancel>
									<AlertDialogAction onClick={() => purgeCleaned({})}>Purge</AlertDialogAction>
								</AlertDialogFooter>
							</AlertDialogContent>
						</AlertDialog>
					)}
				</CardHeader>
				<CardContent>
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Email</TableHead>
								<TableHead>Org ID</TableHead>
								<TableHead>Status</TableHead>
								<TableHead>Expires</TableHead>
								<TableHead className="w-20" />
							</TableRow>
						</TableHeader>
						<TableBody>
							{sessions.length === 0 ? (
								<TableRow>
									<TableCell colSpan={5} className="text-center text-foreground-secondary">
										No demo sessions
									</TableCell>
								</TableRow>
							) : (
								sessions.map((session) => (
									<TableRow key={session._id}>
										<TableCell className="font-medium">{session.email}</TableCell>
										<TableCell className="text-xs text-foreground-secondary font-mono">
											{session.orgId}
										</TableCell>
										<TableCell>
											<Badge variant={statusVariant[session.status] ?? "secondary"}>
												{session.status}
											</Badge>
										</TableCell>
										<TableCell className="text-foreground-secondary">
											{session.isExpired ? (
												<span className="text-severe">expired</span>
											) : (
												formatExpiry(session.expiresAt)
											)}
										</TableCell>
										<TableCell>
											{session.status === "active" || session.status === "expired" ? (
												<AlertDialog>
													<AlertDialogTrigger render={<Button variant="ghost" size="icon-sm" />}>
														<Trash2 className="size-4 text-severe" />
													</AlertDialogTrigger>
													<AlertDialogContent>
														<AlertDialogHeader>
															<AlertDialogTitle>Delete demo session?</AlertDialogTitle>
															<AlertDialogDescription>
																This will clean up the demo session for {session.email}, deleting
																all associated data and the Clerk org/user.
															</AlertDialogDescription>
														</AlertDialogHeader>
														<AlertDialogFooter>
															<AlertDialogCancel>Cancel</AlertDialogCancel>
															<AlertDialogAction
																onClick={() => forceCleanup({ orgId: session.orgId })}
															>
																Delete
															</AlertDialogAction>
														</AlertDialogFooter>
													</AlertDialogContent>
												</AlertDialog>
											) : null}
										</TableCell>
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</CardContent>
			</Card>
		</div>
	)
}

function DemoSessionsSkeleton() {
	return (
		<div className="space-y-6">
			<div className="grid gap-4 sm:grid-cols-3">
				{Array.from({ length: 3 }).map((_, i) => (
					// biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
					<Card key={i} size="sm">
						<CardContent className="pt-4">
							<Skeleton className="h-8 w-20" />
							<Skeleton className="mt-1 h-3 w-24" />
						</CardContent>
					</Card>
				))}
			</div>
			<Card>
				<CardHeader>
					<Skeleton className="h-5 w-32" />
				</CardHeader>
				<CardContent className="space-y-3">
					{Array.from({ length: 5 }).map((_, i) => (
						// biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
						<Skeleton key={i} className="h-10 w-full" />
					))}
				</CardContent>
			</Card>
		</div>
	)
}
